<template>
    <div class="d-flex flex-column gap-2">
        <div>
            <label :for="inputId" class="col-form-label">
                {{ label }}{{ required ? '*' : '' }}
            </label>
            <p class="text-muted small m-0">
                {{
                    disabled
                        ? $t('audits.panels.auditImport.sourceReadOnly', {
                              name: sourceTemplateName,
                          })
                        : snapshot
                        ? $t('audits.panels.auditImport.source', {
                              name: sourceTemplateName,
                          })
                        : $t('audits.panels.auditImport.selectDates', {
                              name: sourceTemplateName,
                          })
                }}
            </p>
        </div>

        <div class="d-flex flex-column gap-2">
            <div
                v-if="!disabled"
                class="ui-filter-bar__seg ui-filter-bar__seg--field align-self-start"
                role="group"
                :aria-label="$t('audits.panels.auditImport.dateMode')"
            >
                <button
                    type="button"
                    :aria-pressed="String(dateMode === 'single')"
                    :disabled="isLoading"
                    @click="setDateMode('single')"
                >
                    {{ $t('audits.panels.auditImport.singleDate') }}
                </button>
                <button
                    type="button"
                    :aria-pressed="String(dateMode === 'range')"
                    :disabled="isLoading"
                    @click="setDateMode('range')"
                >
                    {{ $t('audits.panels.auditImport.dateRange') }}
                </button>
            </div>
            <div class="d-flex align-items-stretch gap-2">
                <DatePicker
                    :key="dateMode"
                    v-model="range"
                    class="audit-import__date"
                    :uid="inputUid"
                    :params="dateParams"
                    :placeholder="$t('audits.panels.auditImport.dates')"
                    :disabled="disabled || isLoading"
                    @update:model-value="isRangeEdited = true"
                />
                <button
                    v-if="!disabled"
                    type="button"
                    class="btn btn-desktop-lg text-nowrap flex-shrink-0"
                    :class="snapshot ? 'btn-white' : 'btn-primary'"
                    :disabled="isLoading || !canImport"
                    @click="importAudits"
                >
                    <span
                        v-if="isLoading"
                        class="spinner-border spinner-border-sm me-1"
                        aria-hidden="true"
                    ></span>
                    {{ importButtonText }}
                </button>
            </div>
            <p v-if="importedText" class="text-muted small text-end m-0">
                {{ importedText }}
            </p>
        </div>

        <div
            v-if="freshResult"
            class="alert mb-0 d-flex flex-wrap align-items-center justify-content-between gap-2"
            :class="disabled ? 'alert-info' : 'alert-warning'"
            role="status"
        >
            <span>
                {{
                    disabled
                        ? $t('audits.panels.auditImport.changedAfterComplete')
                        : $t('audits.panels.auditImport.outOfDate')
                }}
            </span>
            <button
                v-if="!disabled"
                type="button"
                class="btn btn-sm btn-white"
                @click="applyFreshResult"
            >
                {{ $t('audits.panels.auditImport.refresh') }}
            </button>
        </div>

        <div v-if="errorMessage" class="alert alert-danger mb-0" role="alert">
            {{ errorMessage }}
        </div>

        <p v-if="!snapshot && !disabled && required" class="small m-0">
            {{ $t('audits.panels.auditImport.required') }}
        </p>

        <p v-if="!snapshot && disabled" class="text-muted m-0">
            {{ $t('audits.panels.auditImport.notImported') }}
        </p>

        <template v-if="snapshot">
            <p v-if="snapshot.count === 0" class="text-muted m-0">
                {{
                    snapshot.range.from === snapshot.range.to
                        ? $t('audits.panels.auditImport.emptyDate')
                        : $t('audits.panels.auditImport.empty')
                }}
            </p>
            <div
                v-else
                class="d-flex align-items-end justify-content-between gap-2 mt-2"
            >
                <div>
                    <div class="ui-stat-row ui-stat-row--inline">
                        <ul class="ui-stat-row__list">
                            <li class="ui-stat">
                                <h3 class="ui-stat__label">
                                    {{
                                        $t(
                                            'audits.panels.auditImport.completedAudits'
                                        )
                                    }}
                                </h3>
                                <strong class="ui-stat__value">
                                    {{ snapshot.count }}
                                </strong>
                            </li>
                            <li
                                v-if="snapshot.average_percent != null"
                                class="ui-stat"
                            >
                                <h3 class="ui-stat__label">
                                    {{
                                        $t(
                                            'audits.panels.auditImport.averageCompliance'
                                        )
                                    }}
                                </h3>
                                <strong class="ui-stat__value">
                                    {{ snapshot.average_percent }}%
                                </strong>
                            </li>
                        </ul>
                    </div>
                    <p v-if="summaryNote" class="text-muted small m-0 mt-1">
                        {{ summaryNote }}
                    </p>
                </div>
                <button
                    v-if="items.length"
                    type="button"
                    class="btn btn-white btn-sm flex-shrink-0"
                    :aria-expanded="String(isListOpen)"
                    :aria-controls="listId"
                    :aria-label="
                        isListOpen
                            ? $t('audits.panels.auditImport.hideAudits')
                            : $t('audits.panels.auditImport.showAudits')
                    "
                    :title="
                        isListOpen
                            ? $t('audits.panels.auditImport.hideAudits')
                            : $t('audits.panels.auditImport.showAudits')
                    "
                    @click="isListOpen = !isListOpen"
                >
                    <i
                        class="fi"
                        :class="
                            isListOpen ? 'fi-chevron-up' : 'fi-chevron-down'
                        "
                        aria-hidden="true"
                    ></i>
                </button>
            </div>

            <div
                v-if="items.length && isListOpen"
                :id="listId"
                class="table-responsive"
            >
                <table class="ui-table ui-table--compact">
                    <thead>
                        <tr>
                            <th scope="col">
                                {{ $t('audits.panels.auditImport.audit') }}
                            </th>
                            <th scope="col">
                                {{ $t('audits.panels.auditImport.date') }}
                            </th>
                            <th
                                v-if="!snapshot.scores_hidden"
                                scope="col"
                                class="ui-table__cell--number"
                            >
                                {{ $t('audits.panels.auditImport.compliance') }}
                            </th>
                            <th scope="col" class="ui-table__cell--chevron">
                                <span class="visually-hidden">
                                    {{
                                        $t('audits.panels.auditImport.details')
                                    }}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in items"
                            :key="item.hash"
                            class="ui-table__row--link"
                        >
                            <th scope="row">
                                <a
                                    :href="auditLink(item.hash)"
                                    class="ui-table__link"
                                    @click.exact.prevent="
                                        openAudit(item, $event)
                                    "
                                >
                                    {{ item.name }}
                                </a>
                                <span class="ui-table__meta">
                                    <span>
                                        {{
                                            [item.auditor_name, item.area_name]
                                                .filter(Boolean)
                                                .join(' · ') || '-'
                                        }}
                                    </span>
                                    <span
                                        v-if="item.unpublished"
                                        class="ui-chip ui-chip--small"
                                    >
                                        {{
                                            $t(
                                                'audits.panels.auditImport.notPublished'
                                            )
                                        }}
                                    </span>
                                </span>
                            </th>
                            <td class="text-nowrap">
                                {{ formatDisplayDate(item.date) }}
                            </td>
                            <td
                                v-if="!snapshot.scores_hidden"
                                class="ui-table__cell--number text-nowrap"
                            >
                                {{
                                    item.percent == null
                                        ? '-'
                                        : `${item.percent}%`
                                }}
                            </td>
                            <td class="ui-table__cell--chevron">
                                <i
                                    class="fi fi-chevron-right ui-table__chevron"
                                    aria-hidden="true"
                                ></i>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p v-if="snapshot.truncated" class="text-muted small m-0">
                {{
                    $t('audits.panels.auditImport.truncated', {
                        shown: items.length,
                        total: snapshot.count,
                    })
                }}
            </p>
        </template>
    </div>
</template>

<script>
import moment from 'moment';
import DatePicker from '../fields/DatePicker.vue';
import {
    formatDisplayDate,
    formatLocalizedDateTime,
    normalizeDateRangeInput,
} from '@/utils/dateUtils.js';

const DATE_ONLY_FORMAT = 'YYYY-MM-DD';

let nextFieldId = 0;

/** Returns the audit date as YYYY-MM-DD. The API sends it as a UTC midnight timestamp. */
const toDateOnly = (value) => {
    if (!value) return null;
    if (moment(value, DATE_ONLY_FORMAT, true).isValid()) return value;
    const date = moment.utc(value, moment.ISO_8601, true);
    return date.isValid() ? date.format(DATE_ONLY_FORMAT) : null;
};

/**
 * Perform and read-only view of an audit import field. It imports the completed audits of
 * the source template for the audit site and a date range, and saves the result as a
 * snapshot in the field value. When a saved snapshot opens, it imports again with the same
 * range. If the result changed, it shows a note, and an editable audit can take the new result.
 */
export default {
    name: 'AuditImportField',
    components: { DatePicker },
    props: {
        // Saved snapshot, or null before the first import
        modelValue: {
            type: Object,
            default: null,
        },
        label: {
            type: String,
            default: '',
        },
        required: {
            type: Boolean,
            default: false,
        },
        // Field params: source_template_id, source_template_name
        params: {
            type: Object,
            default: () => ({}),
        },
        // The audit that holds the field: { date, siteId, hash }
        auditContext: {
            type: Object,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'open-audit'],
    data() {
        nextFieldId += 1;
        return {
            fieldId: `audit-import-${nextFieldId}`,
            // 'single': one date, 'range': a from and to date
            dateMode: 'single',
            // A YYYY-MM-DD string in single mode, or [from, to] in range mode
            range: null,
            isRangeEdited: false,
            isLoading: false,
            errorMessage: '',
            // A newer result found when a saved snapshot opens
            freshResult: null,
            isListOpen: true,
        };
    },
    computed: {
        snapshot() {
            return this.modelValue?.range ? this.modelValue : null;
        },
        items() {
            return Array.isArray(this.snapshot?.items)
                ? this.snapshot.items
                : [];
        },
        sourceTemplateName() {
            return (
                this.snapshot?.source_template?.name ||
                this.params?.source_template_name ||
                ''
            );
        },
        hostDate() {
            return toDateOnly(this.auditContext?.date);
        },
        selectedRange() {
            const range = normalizeDateRangeInput(this.range);
            const from = toDateOnly(range?.start);
            const to = toDateOnly(range?.end);
            return from && to ? { from, to } : null;
        },
        canImport() {
            return (
                !!this.params?.source_template_id &&
                !!this.selectedRange &&
                !!this.auditContext
            );
        },
        inputUid() {
            return this.fieldId;
        },
        inputId() {
            return `dp-input-${this.inputUid}`;
        },
        listId() {
            return `${this.fieldId}-list`;
        },
        dateParams() {
            return {
                rangeMode: this.dateMode === 'range',
                teleport: true,
                presetDates: false,
                multiCalendars: false,
            };
        },
        summaryNote() {
            if (!this.snapshot) return '';
            const notes = [];
            if (this.snapshot.in_progress_count > 0) {
                notes.push(
                    this.$t('audits.panels.auditImport.inProgress', {
                        count: this.snapshot.in_progress_count,
                    })
                );
            }
            if (this.snapshot.scores_hidden) {
                notes.push(this.$t('audits.panels.auditImport.scoresHidden'));
            }
            return notes.join(' · ');
        },
        importButtonText() {
            if (this.isLoading) {
                return this.$t('audits.panels.auditImport.importing');
            }
            return this.snapshot
                ? this.$t('audits.panels.auditImport.importAgain')
                : this.$t('audits.panels.auditImport.import');
        },
        importedText() {
            if (!this.snapshot?.imported_at) return '';
            const date = formatLocalizedDateTime(this.snapshot.imported_at);
            const name = this.snapshot.imported_by?.name;
            return name
                ? this.$t('audits.panels.auditImport.importedBy', {
                      date,
                      name,
                  })
                : this.$t('audits.panels.auditImport.importedAt', { date });
        },
    },
    watch: {
        // Before the first import, the range follows the audit date until the auditor changes it.
        hostDate(date) {
            if (!this.snapshot && !this.isRangeEdited && date) {
                this.setRange(date, date);
            }
        },
    },
    methods: {
        formatDisplayDate,
        // Sets the picker value for the current date mode.
        setRange(from, to) {
            this.range = this.dateMode === 'range' ? [from, to] : from;
        },
        setDateMode(mode) {
            if (mode === this.dateMode) return;
            const from = this.selectedRange?.from || this.hostDate;
            const to = this.selectedRange?.to || from;
            this.dateMode = mode;
            this.setRange(from, mode === 'range' ? to : from);
        },
        openAudit(item, event) {
            this.$emit('open-audit', { item, trigger: event.currentTarget });
        },
        auditLink(hash) {
            const companyHash =
                this.$route?.name === 'Company Detail'
                    ? this.$route.params?.hash
                    : null;
            const location = companyHash
                ? {
                      name: 'AdminAuditOverview',
                      params: { companyHash, auditHash: hash },
                  }
                : { name: 'Audits', params: { hash } };
            return this.$router.resolve(location).href;
        },
        fetchImport({ from, to }) {
            const siteId = this.auditContext?.siteId;
            return this.$getData('audit/import', {
                source_template_id: this.params.source_template_id,
                host_site_id: siteId ? siteId : 'none',
                from,
                to,
                ...(this.auditContext?.hash && {
                    host_audit_hash: this.auditContext.hash,
                }),
            }).then((response) => {
                if (!response?.success) {
                    throw new Error(
                        response?.message ||
                            this.$t('audits.panels.auditImport.loadError')
                    );
                }
                return response.data;
            });
        },
        toSnapshot(result) {
            const { generated_at, ...snapshot } = result;
            return {
                ...snapshot,
                imported_at: moment.utc().toISOString(),
                imported_by: {
                    id: this.$userStore.user?.id ?? null,
                    name: this.$userStore.getFullName,
                },
            };
        },
        importAudits() {
            if (!this.canImport || this.isLoading) return;
            this.isLoading = true;
            this.errorMessage = '';
            this.fetchImport(this.selectedRange)
                .then((result) => {
                    this.freshResult = null;
                    this.isListOpen = true;
                    this.$emit('update:modelValue', this.toSnapshot(result));
                })
                .catch((error) => {
                    this.errorMessage =
                        error?.message ||
                        this.$t('audits.panels.auditImport.loadError');
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        applyFreshResult() {
            if (!this.freshResult) return;
            this.$emit('update:modelValue', this.toSnapshot(this.freshResult));
            this.freshResult = null;
        },
        // Imports again with the saved range and keeps the result when the fingerprint changed.
        checkForChanges() {
            if (!this.snapshot || !this.params?.source_template_id) return;
            if (!this.auditContext) return;
            this.fetchImport(this.snapshot.range)
                .then((result) => {
                    if (result.fingerprint !== this.snapshot?.fingerprint) {
                        this.freshResult = result;
                    }
                })
                .catch(() => {
                    // The saved snapshot stays visible. The auditor can import again.
                });
        },
    },
    mounted() {
        if (this.snapshot) {
            const { from, to } = this.snapshot.range;
            this.dateMode = from === to ? 'single' : 'range';
            this.setRange(from, to);
            this.checkForChanges();
        } else if (this.hostDate) {
            this.setRange(this.hostDate, this.hostDate);
        }
    },
};
</script>

<style scoped>
/* Layout only: the picker fills the row and leaves room for the Import button. */
.audit-import__date {
    flex: 1 1 auto;
    min-width: 0;
}
</style>
